import * as React from 'react';
import { AdminScreen } from '@app/Screens/types';
import { StatusBadge } from '@app/Screens/StatusBadge';
import { relativeTime, formatCoord, formatSpeed, playingFor } from '@app/Screens/format';
import { colors } from '@app/components/designTokens';

interface ScreensTableProps {
  screens: AdminScreen[];
  onSelect: (screenId: number) => void;
}

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '12px 16px',
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  color: colors.subtle,
  borderBottom: `1px solid ${colors.border}`,
  whiteSpace: 'nowrap'
};

const td: React.CSSProperties = {
  padding: '14px 16px',
  fontSize: 14,
  color: colors.body,
  borderBottom: `1px solid ${colors.border}`,
  verticalAlign: 'middle'
};

export const ScreensTable: React.FunctionComponent<ScreensTableProps> = ({ screens, onSelect }) => {
  const [hovered, setHovered] = React.useState<number | null>(null);

  if (screens.length === 0) {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: colors.subtle }}>
        No screens match the current filters.
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 880 }}>
        <thead>
          <tr>
            <th style={th}>Status</th>
            <th style={th}>Screen</th>
            <th style={th}>Last seen</th>
            <th style={th}>Now playing</th>
            <th style={th}>Driver</th>
            <th style={th}>Speed</th>
            <th style={th}>Location</th>
          </tr>
        </thead>
        <tbody>
          {screens.map(s => {
            const disabled = s.enabled === false;
            return (
              <tr
                key={s.screenId}
                onClick={() => onSelect(s.screenId)}
                onMouseEnter={() => setHovered(s.screenId)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  cursor: 'pointer',
                  background: hovered === s.screenId ? '#f8fafc' : 'transparent',
                  opacity: disabled ? 0.55 : 1
                }}
              >
                <td style={td}>
                  <StatusBadge status={s.status} pulse />
                </td>
                <td style={td}>
                  <div style={{ fontWeight: 600 }}>{s.displayName || `Screen #${s.screenId}`}</div>
                  <div style={{ fontSize: 12, color: colors.subtle }}>
                    {s.clientId ? s.clientId : `id ${s.screenId}`}
                    {disabled && (
                      <span style={{ marginLeft: 8, color: colors.danger, fontWeight: 600 }}>disabled</span>
                    )}
                  </div>
                </td>
                <td style={td}>{relativeTime(s.lastSeenAt)}</td>
                <td style={td}>
                  {s.currentAdName ? (
                    <>
                      <div>{s.currentAdName}</div>
                      {s.adStartedAt && (
                        <div style={{ fontSize: 12, color: colors.subtle }}>
                          for {playingFor(s.adStartedAt)}
                        </div>
                      )}
                    </>
                  ) : (
                    <span style={{ color: colors.placeholder }}>—</span>
                  )}
                </td>
                <td style={td}>{s.pairedDriverName || <span style={{ color: colors.placeholder }}>—</span>}</td>
                <td style={td}>{formatSpeed(s.speedKmh)}</td>
                <td style={td}>
                  <span style={{ fontSize: 13, color: colors.muted, fontFamily: 'monospace' }}>
                    {formatCoord(s.latitude, s.longitude)}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
